import { ThunkDispatch } from "core-integration/src/store";
import { bootstrap } from "core-integration/src/store/bootstrap/thunks";
import {
  getPlayerData,
  getPlayerUpdateError,
  getRegisterError,
} from "core-integration/src/store/player/reducers";
import {
  otpSendEmailChange,
  otpValidateEmailChange,
  playerUpdate,
  register,
} from "core-integration/src/store/player/thunks";
import { getRegions } from "core-integration/src/store/regions/reducers";
import { fetchRegions } from "core-integration/src/store/regions/thunks";
import { trackEvent } from "fathom-client";
import range from "lodash/range";
import sortBy from "lodash/sortBy";
import values from "lodash/values";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import AltiboxLogo from "../../img/logos/altibox-logo.svg";
import EliteSerienLogo from "../../img/logos/es-logo.svg";
import NorskTippingLogo from "../../img/logos/norsk-tipping.svg";
import ObosLogo from "../../img/logos/obos-logo.svg";
import TV2Logo from "../../img/logos/tv2-logo.svg";
import { IPlayer } from "../../types";
import { loadFathomTrackingId } from "../../utils/fathom";
import { getSite } from "../../utils/sites";
import Alert from "../Alert";
import Button from "../Button";
import Copy from "../Copy";
import {
  CheckboxField,
  FieldFeedback,
  InputField,
  SelectField,
} from "../FieldRenderers";
import { Main, Wrapper } from "../Layout";
import Title from "../Title";

const DOBFieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: 0;
`;

const DOBFieldsetInner = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: ${({ theme }) => theme.space[2]};
    margin-left: -${({ theme }) => theme.space[2]};
    margin-right: -${({ theme }) => theme.space[2]};
  }
`;

const DOBLegend = styled.legend`
  display: block;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.space[1]};
  color: ${({ theme }) => theme.colors.blueDarker1};
  font-size: 1.5rem;
  font-weight: bold;
  cursor: pointer;
`;

const DOBCol = styled.div`
  margin-top: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    width: calc(100% / 3);
    margin-top: 0;
    padding-left: ${({ theme }) => theme.space[2]};
    padding-right: ${({ theme }) => theme.space[2]};
  }
`;

type Props = WithTranslation;

interface IState {
  birthDay: number;
  birthMonth: number;
  birthYear: number;
  email: string;
  emailEliteserien: boolean;
  emailFavourite: boolean;
  emailNorsk: boolean;
  emailObos: boolean;
  emailTV2: boolean;
  emailAltibox: boolean;
  firstName: string;
  lastName: string;
  region: number;
  errorDob: string;
  errorEmail: string;
  errorFName: string;
  errorLName: string;
  errorPass: string;
  errorRegion: string;
  fieldFirstNDisabled: boolean;
  fieldLasttNDisabled: boolean;
  otpToken: string;
  otpSent: boolean;
  otpVerified: boolean;
  errorOtp: string;
}

const Register = ({ t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();
  const player = useSelector(getPlayerData) as IPlayer | null;
  const registerError = useSelector(getRegisterError);
  const updateError = useSelector(getPlayerUpdateError);
  const regions = useSelector(getRegions);

  const doBootstrap = () => dispatch(bootstrap());
  const registerPlayer = (data: any) => dispatch(register(data));
  const updatePlayer = (data: any) => dispatch(playerUpdate(data));
  const sendOtpEmailChange = (data: { email: string }) =>
    dispatch(otpSendEmailChange(data));
  const validateOtpEmailChange = (data: { email: string; token: string }) =>
    dispatch(otpValidateEmailChange(data));
  const fetchRegionData = () => dispatch(fetchRegions());

  const errorsTxt: { [key: string]: string } = {
    EDob: t("register.errors.dob", "</p> not valid"),
    EEmail: t("register.errors.email", "An email is required"),
    EFName: t("register.errors.fname", "First name is required"),
    ELName: t("register.errors.lname", "Last name is required"),
    ERegion: t("register.errors.region", "A region is required"),
    EValidDob: t("register.errors.validDob", "A correct </p> is required"),
    EvalidEmail: t(
      "register.errors.validEmail",
      "This Email address is already registered",
    ),
    EOtp: t("register.errors.otp", "Code is required"),
    EValidOtp: t("register.errors.validOtp", "Invalid code"),
  };

  const months: { [key: string]: string } = {
    1: t("months.janShort", "Jan"),
    2: t("months.febShort", "Feb"),
    3: t("months.marShort", "Mar"),
    4: t("months.aprShort", "Apr"),
    5: t("months.mayShort", "May"),
    6: t("months.junShort", "Jun"),
    7: t("months.julShort", "Jul"),
    8: t("months.augShort", "Aug"),
    9: t("months.sepShort", "Sep"),
    10: t("months.octShort", "Oct"),
    11: t("months.novShort", "Nov"),
    12: t("months.decShort", "Dec"),
  };

  const [state, setState] = useState<IState>({
    birthDay:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[2])
        : 0,
    birthMonth:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[1])
        : 0,
    birthYear:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[0])
        : 0,
    email: player ? player.email : "",
    emailAltibox: player ? player.email_altibox : false,
    emailEliteserien: player ? player.email_eliteserien : false,
    emailNorsk: player ? player.email_norsk : false,
    emailObos: player ? player.email_obos : false,
    emailTV2: player ? player.email_tv2 : false,
    emailFavourite: player ? player.email_favourite : false,
    errorDob: "",
    errorEmail: "",
    errorFName: "",
    errorLName: "",
    errorPass: "",
    errorRegion: "",
    firstName: player ? player.first_name : "",
    lastName: player ? player.last_name : "",
    region: player ? player.region : 0,
    fieldFirstNDisabled: Boolean(player?.first_name),
    fieldLasttNDisabled: Boolean(player?.last_name),
    otpToken: "",
    otpSent: false,
    otpVerified: false,
    errorOtp: "",
  });

  const getMode = () => {
    if (!player) {
      return "register";
    }
    return player.dirty ? "confirm" : "update";
  };

  const getRequiredFields: () => Array<keyof IState> = () => {
    switch (getMode()) {
      case "register":
        return [];
      case "confirm":
      case "update":
        return [
          "birthDay",
          "birthMonth",
          "birthYear",
          "email",
          "firstName",
          "lastName",
          "region",
        ];
    }
  };

  const apiDataFromState = () => {
    switch (getMode()) {
      case "register":
        return {};
      case "confirm":
      case "update":
        return {
          date_of_birth: `${state.birthYear}-${state.birthMonth}-${state.birthDay}`,
          email: state.email,
          email_altibox: state.emailAltibox,
          email_eliteserien: state.emailEliteserien,
          email_norsk: state.emailNorsk,
          email_obos: state.emailObos,
          email_tv2: state.emailTV2,
          email_favourite: state.emailFavourite,
          first_name: state.firstName,
          last_name: state.lastName,
          region: state.region,
        };
    }
  };

  const handleBirthDayChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, birthDay: parseInt(e.currentTarget.value, 10) });

  const handleBirthMonthChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, birthMonth: parseInt(e.currentTarget.value, 10) });

  const handleBirthYearChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, birthYear: parseInt(e.currentTarget.value, 10) });

  const handleEmailChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, email: e.currentTarget.value });

  const handleEmailEliteserienChange = (
    e: React.SyntheticEvent<HTMLInputElement>,
  ) =>
    setState({
      ...state,
      emailEliteserien: e.currentTarget.checked ? true : false,
    });

  const handleEmailFavouriteChange = (
    e: React.SyntheticEvent<HTMLInputElement>,
  ) =>
    setState({
      ...state,
      emailFavourite: e.currentTarget.checked ? true : false,
    });

  const handleEmailObosChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, emailObos: e.currentTarget.checked ? true : false });

  const handleEmailNorskChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({
      ...state,
      emailNorsk: e.currentTarget.checked ? true : false,
    });

  const handleEmailTV2Change = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, emailTV2: e.currentTarget.checked ? true : false });

  const handleEmailAltiboxChange = (
    e: React.SyntheticEvent<HTMLInputElement>,
  ) =>
    setState({
      ...state,
      emailAltibox: e.currentTarget.checked ? true : false,
    });

  const handleFirstNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, firstName: e.currentTarget.value });

  const handleLastNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, lastName: e.currentTarget.value });

  const handleRegionChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, region: parseInt(e.currentTarget.value, 10) });

  const handleOtpTokenChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, otpToken: e.currentTarget.value });

  const handleSendOtp = async () => {
    if (!state.email) {
      setState({ ...state, errorEmail: errorsTxt.EEmail });
      return;
    }
    try {
      await sendOtpEmailChange({ email: state.email });
      setState({ ...state, otpSent: true, errorOtp: "" });
    } catch (e) {
      // Error is handled by thunk/reducer and shown in handleServerErrors
    }
  };

  const handleVerifyOtp = async () => {
    if (!state.otpToken) {
      setState({ ...state, errorOtp: errorsTxt.EOtp });
      return;
    }
    try {
      await validateOtpEmailChange({
        email: state.email,
        token: state.otpToken,
      });
      setState({ ...state, otpVerified: true, otpSent: false, errorOtp: "" });
    } catch (e) {
      setState({ ...state, errorOtp: errorsTxt.EValidOtp });
    }
  };

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const mode = getMode();
    if (mode === "register") {
      try {
        await registerPlayer(apiDataFromState());
      } catch (e) {
        window.scrollTo(0, 0); // To see error if failure ...
        return;
      }
      await doBootstrap();

      // Add fathom trackEvent when a new user
      // registers and navigates to "/squad-selection"
      const site = getSite();
      const trackEventLabels = {
        main_nb: "Registrert bruker",
        main_en: "Registered user",
        tv2: "Registrert TV 2 bruker",
      };
      if (trackEventLabels[site]) {
        trackEvent(trackEventLabels[site]);
      }

      navigate("/squad-selection");
    } else if (mode === "confirm") {
      try {
        await updatePlayer(apiDataFromState());
      } catch (e) {
        window.scrollTo(0, 0); // To see error if failure ...
        return;
      }
      await doBootstrap();
      navigate("/squad-selection");
    } else if (mode === "update") {
      try {
        await updatePlayer(apiDataFromState());
      } catch (e) {
        window.scrollTo(0, 0); // To see error if failure ...
        return;
      }
      await doBootstrap();
      // Maybe this should be back to my team, maybe our LoggedIn component
      navigate("/");
    }
  };

  useEffect(() => {
    fetchRegionData();
    // Upon initial page load...
    loadFathomTrackingId();
  }, []);

  const getNorwayRegions = () => {
    const regs = regions;
    const norStart = 286;
    const nor = regs.filter((reg) => reg.code >= norStart);
    return sortBy(nor, ["name"]);
  };
  const getIntRegions = () => {
    const regs = regions;
    const norStart = 286;
    const int = regs.filter((reg) => reg.code < norStart);
    return sortBy(int, ["name"]);
  };

  const handleFirstnameBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorFName: errorsTxt.EFName });
    } else {
      setState({ ...state, errorFName: "" });
    }
  };

  const handleLastnameBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorLName: errorsTxt.ELName });
    } else {
      setState({ ...state, errorLName: "" });
    }
  };

  const handleEmailBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorEmail: errorsTxt.EEmail });
    } else {
      setState({ ...state, errorEmail: "" });
    }
  };

  const handleRegionBlur = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    if (!e.currentTarget.value || e.currentTarget.value === "0") {
      setState({ ...state, errorRegion: errorsTxt.ERegion });
    } else {
      setState({ ...state, errorRegion: "" });
    }
  };

  const handleDobBlur = (_: React.SyntheticEvent<HTMLSelectElement>) => {
    const year = state.birthYear;
    const month = state.birthMonth;
    const day = state.birthDay;
    if (year > 0 && month > 0 && day > 0) {
      if (moment(new Date(year, month - 1, day)).isValid()) {
        const today = new Date();
        const birthDate = new Date(year, month - 1, day);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 13) {
          setState({ ...state, errorDob: errorsTxt.EDob });
        } else {
          setState({ ...state, errorDob: "" });
        }
      } else {
        setState({ ...state, errorDob: errorsTxt.EDob });
      }
    }
  };

  const handleServerErrors = () => {
    const errors = getMode() === "register" ? registerError : updateError;
    const formattedErrors = [];
    if (errors) {
      if (
        errors.badRequest &&
        errors.badRequest.email &&
        errors.badRequest.email[0].code === "duplicate"
      ) {
        formattedErrors.push(errorsTxt.EvalidEmail);
      }
      if (
        errors.badRequest &&
        errors.badRequest.email &&
        errors.badRequest.email[0].code === "blank"
      ) {
        formattedErrors.push(errorsTxt.EEmail);
      }
      if (
        errors.badRequest &&
        errors.badRequest.last_name &&
        errors.badRequest.last_name[0].code === "blank"
      ) {
        formattedErrors.push(errorsTxt.EFName);
      }
      if (
        errors.badRequest &&
        errors.badRequest.last_name &&
        errors.badRequest.last_name[0].code === "blank"
      ) {
        formattedErrors.push(errorsTxt.ELName);
      }
      if (
        errors.badRequest &&
        errors.badRequest.date_of_birth &&
        errors.badRequest.date_of_birth[0].code === "invalid"
      ) {
        formattedErrors.push(errorsTxt.EValidDob);
      }
      if (
        errors.badRequest &&
        errors.badRequest.region &&
        errors.badRequest.region[0].code === "does_not_exist"
      ) {
        formattedErrors.push(errorsTxt.ERegion);
      }

      return formattedErrors;
    }
  };

  const mode = getMode();
  const title = {
    register: t("register.title.registration", "Registration"),
    confirm: t("register.title.confirm", "Confirm your details"),
    update: t("register.title.update", "Confirm your details"),
  };
  const buttonText = {
    register: t("register.label.register", "Register"),
    confirm: t("register.label.confirm", "Update"),
    update: t("register.label.update", "Update"),
  };
  const isEmailChanged = Boolean(player && state.email !== player.email);
  const disabled =
    getRequiredFields().some((e) => !state[e]) ||
    (isEmailChanged && !state.otpVerified);
  const errors = handleServerErrors();

  return (
    <Wrapper>
      <Main>
        <Box mx={2}>
          <Title>{title[mode]}</Title>
        </Box>

        {mode === "register" ? (
          <Box mx={2}>
            <p>
              <Trans i18nKey="register.registerAtTV2Play">
                To register a new account, please visit{" "}
                <a
                  href="https://id.tv2.no/login"
                  target="_blank"
                  rel="noreferrer"
                >
                  TV 2 Play
                </a>
                .
              </Trans>{" "}
            </p>
          </Box>
        ) : (
          <>
            <Box mx={2}>
              <p>
                {t(
                  "register.explain",
                  "Fields marked with an asterisk (*) are required.",
                )}
              </p>
            </Box>
            {errors && errors.length > 0 && (
              <Alert type="error">{values(errors).map((r) => r)}</Alert>
            )}
            <Copy>
              <form onSubmit={handleSubmit}>
                <Box>
                  <InputField
                    required
                    id="ismRegisterEmail"
                    type="email"
                    hasErrors={!!state.errorEmail}
                    label={t("register.label.email", "Email")}
                    value={state.email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    disabled={state.otpSent || state.otpVerified}
                  />
                  {state.errorEmail && (
                    <FieldFeedback>{state.errorEmail}</FieldFeedback>
                  )}
                  {isEmailChanged && !state.otpVerified && !state.otpSent && (
                    <Box mt={2}>
                      <Button type="button" onClick={handleSendOtp}>
                        {t("register.label.verifyEmail", "Verify Email")}
                      </Button>
                    </Box>
                  )}
                  {state.otpSent && (
                    <Box mt={2}>
                      <p>
                        {t(
                          "register.otp.explain",
                          "A code has been emailed to you. Enter it here to authorise this action.",
                        )}
                      </p>
                      <InputField
                        id="ismRegisterOtp"
                        label={t("register.label.otp", "Enter code")}
                        value={state.otpToken}
                        onChange={handleOtpTokenChange}
                        hasErrors={!!state.errorOtp}
                      />
                      {state.errorOtp && (
                        <FieldFeedback>{state.errorOtp}</FieldFeedback>
                      )}
                      <Box mt={2}>
                        <Button type="button" onClick={handleVerifyOtp}>
                          {t("register.label.confirmOtp", "Confirm code")}
                        </Button>
                      </Box>
                    </Box>
                  )}
                  {state.otpVerified && (
                    <Box mt={2}>
                      <Alert type="success">
                        {t(
                          "register.message.emailVerified",
                          "Email verified successfully!",
                        )}
                      </Alert>
                    </Box>
                  )}
                </Box>
                <Box mt={2}>
                  <InputField
                    id="ismRegisterFirstName"
                    hasErrors={!!state.errorFName}
                    label={t("register.label.fName", "First Name")}
                    value={state.firstName}
                    onChange={handleFirstNameChange}
                    onBlur={handleFirstnameBlur}
                    disabled={state.fieldFirstNDisabled}
                  />
                  {state.errorFName && (
                    <FieldFeedback>{state.errorFName}</FieldFeedback>
                  )}
                </Box>
                <Box mt={2}>
                  <InputField
                    id="ismRegisterLastName"
                    label={t("register.label.lName", "Last Name")}
                    hasErrors={!!state.errorLName}
                    value={state.lastName}
                    onChange={handleLastNameChange}
                    onBlur={handleLastnameBlur}
                    disabled={state.fieldLasttNDisabled}
                  />
                  {state.errorLName && (
                    <FieldFeedback>{state.errorLName}</FieldFeedback>
                  )}
                </Box>
                <Box mt={2}>
                  <DOBFieldset>
                    <DOBLegend>Date of birth</DOBLegend>
                    <span>
                      {t("register.hint.dob", "Game open to over 13 only")}
                    </span>
                    <DOBFieldsetInner>
                      <DOBCol>
                        <SelectField
                          labelVisbility={false}
                          id="ismRegisterBirthDay"
                          value={state.birthDay}
                          onChange={handleBirthDayChange}
                          label={t("register.label.day", "Day")}
                          onBlur={handleDobBlur}
                        >
                          <option
                            value="0"
                            aria-selected={!state.birthDay}
                            disabled
                            defaultValue="0"
                          >
                            {t("register.label.day", "Day")}
                          </option>
                          {range(1, 32).map((d) => (
                            <option
                              value={d}
                              aria-selected={d === state.birthDay}
                              key={d}
                            >
                              {d}
                            </option>
                          ))}
                        </SelectField>
                      </DOBCol>
                      <DOBCol>
                        <SelectField
                          labelVisbility={false}
                          id="ismRegisterBirthMonth"
                          value={state.birthMonth}
                          onChange={handleBirthMonthChange}
                          label={t("register.label.month", "Month")}
                          onBlur={handleDobBlur}
                        >
                          <option
                            value="0"
                            aria-selected={!state.birthMonth}
                            disabled
                            defaultValue="0"
                          >
                            {t("register.label.month", "Month")}
                          </option>
                          {Object.keys(months).map((m) => (
                            <option
                              value={m}
                              aria-selected={
                                parseInt(m, 10) === state.birthMonth
                              }
                              key={m}
                            >
                              {months[m]}
                            </option>
                          ))}
                        </SelectField>
                      </DOBCol>
                      <DOBCol>
                        <SelectField
                          labelVisbility={false}
                          value={state.birthYear}
                          onChange={handleBirthYearChange}
                          label={t("register.label.year", "Year")}
                          id="ismRegisterBirthYear"
                          onBlur={handleDobBlur}
                        >
                          <option
                            value="0"
                            aria-selected={!state.birthYear}
                            disabled
                            defaultValue="0"
                          >
                            {t("register.label.year", "Year")}
                          </option>
                          {range(new Date().getFullYear() - 13, 1900).map(
                            (y) => (
                              <option
                                value={y}
                                aria-selected={y === state.birthYear}
                                key={y}
                              >
                                {y}
                              </option>
                            ),
                          )}
                        </SelectField>
                      </DOBCol>
                    </DOBFieldsetInner>
                    {state.errorDob && (
                      <FieldFeedback>{state.errorDob}</FieldFeedback>
                    )}
                  </DOBFieldset>
                </Box>
                <Box mt={2}>
                  {state.errorRegion && (
                    <FieldFeedback>{state.errorRegion}</FieldFeedback>
                  )}
                  <SelectField
                    value={state.region}
                    onChange={handleRegionChange}
                    label={t("register.label.regionn", "Region")}
                    id="ismRegisterRegion"
                    onBlur={handleRegionBlur}
                  >
                    <option value="0" aria-selected={!state.region}>
                      {t("register.label.select", "Select")}
                    </option>
                    <optgroup
                      label={t<string>("register.label.norway", "Norway")}
                    >
                      {values(getNorwayRegions()).map((r) => (
                        <option
                          value={r.id}
                          aria-selected={r.id === state.region}
                          key={r.id}
                        >
                          {r.name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup
                      label={t<string>(
                        "register.label.international",
                        "International",
                      )}
                    >
                      {values(getIntRegions()).map((r) => (
                        <option
                          value={r.id}
                          aria-selected={r.id === state.region}
                          key={r.id}
                        >
                          {r.name}
                        </option>
                      ))}
                    </optgroup>
                  </SelectField>
                </Box>
                <Box mt={2}>
                  <p>
                    {t(
                      "register.emailText",
                      "Receive information and newsletter from:",
                    )}
                  </p>
                  <CheckboxField
                    checked={state.emailFavourite}
                    id="ismRegisterEmailFavourite"
                    label={t("register.label.emailClub", "Your favourite club")}
                    onChange={handleEmailFavouriteChange}
                  />
                  <CheckboxField
                    checked={state.emailTV2}
                    id="ismRegisterEmailTV2"
                    label={t("register.label.emailTV2", "TV2")}
                    onChange={handleEmailTV2Change}
                    logo={TV2Logo}
                    hideLabel={true}
                  />
                  <CheckboxField
                    checked={state.emailEliteserien}
                    id="ismRegisterEmailEliteserien"
                    label={t("register.label.emailElite", "Eliteserien")}
                    onChange={handleEmailEliteserienChange}
                    logo={EliteSerienLogo}
                    hideLabel={true}
                  />
                  <CheckboxField
                    checked={state.emailAltibox}
                    id="ismRegisterEmailAltibox"
                    label={t("register.label.emailAlti", "Altibox")}
                    onChange={handleEmailAltiboxChange}
                    logo={AltiboxLogo}
                    hideLabel={true}
                  />
                  <CheckboxField
                    checked={state.emailObos}
                    id="ismRegisterEmailObos"
                    label={t("register.label.emailObos", "Obos")}
                    onChange={handleEmailObosChange}
                    logo={ObosLogo}
                    hideLabel={true}
                  />
                  <CheckboxField
                    checked={state.emailNorsk}
                    id="ismRegisterEmailNorsk"
                    label={t("register.label.emailNorsk", "Norsk Tipping")}
                    onChange={handleEmailNorskChange}
                    logo={NorskTippingLogo}
                    hideLabel={true}
                  />
                </Box>
                <Box mt={2} mb={8}>
                  <Button type="submit" disabled={disabled}>
                    {buttonText[mode]}
                  </Button>
                </Box>
              </form>
            </Copy>
          </>
        )}
        {(mode === "confirm" || mode === "update") && player && (
          <Box mx={2}>
            <p>
              <Trans i18nKey="common:register.helpAtTV2Play" t={t}>
                For account or login assistance, visit the{" "}
                <a
                  href="https://hjelp.tv2.no/hovedkategori/konto-og-abonnement/passord"
                  target="_blank"
                  rel="noreferrer"
                >
                  TV 2 Play support page
                </a>
                .
              </Trans>
            </p>
          </Box>
        )}
      </Main>
    </Wrapper>
  );
};

export { Register as RegisterTest };

export default withTranslation()(Register);
